'use client';

import type { FC } from 'react';

import { partnerPageGA, shopReferral } from '@/lib/gtm';
import { cn } from '@/lib/utils';
import mapPrismicImageProxy from '@/utils/prismic/mapPrismicImageProxy';

import { Card, type CardProps } from '@/components/atoms/Card';
import { Title } from '@/components/atoms/Typography/Title';

export type SubCategoryCardTemplateType = {
    link: string;
    title: string;
    background: string;
    typename?: string;
};

export interface SubCategoriesCardTemplateProps
    extends Omit<CardProps, 'children'> {
    partnerName: string;
    shopLinkPlacement: string;
    category: SubCategoryCardTemplateType;
    'data-test-id'?: string;
}

const SubCategoriesCardTemplate: FC<SubCategoriesCardTemplateProps> = ({
    category,
    partnerName,
    shopLinkPlacement,
    ...cardProps
}) => {
    const { link, title, background, typename } = category;

    const handleGA = () => {
        if (partnerName) {
            partnerPageGA(typename ?? '', partnerName, link);
        }
        shopReferral(title, shopLinkPlacement);
    };

    return (
        <a
            className={cn(
                'group relative flex cursor-pointer justify-center overflow-hidden',
                'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:overflow-hidden before:rounded-2xl before:bg-black before:opacity-30',
            )}
            href={link}
            target="_blank"
            onClick={handleGA}
        >
            <Card
                variant="filled"
                className={cn(
                    'flex min-h-[120px] w-full items-center justify-center bg-cover bg-center transition-all duration-300 md:min-h-[149px] xl:min-h-[250px]',
                )}
                style={{
                    backgroundImage: `url(${mapPrismicImageProxy(background)})`,
                }}
                {...cardProps}
            >
                <Title
                    size="m"
                    className="z-10 text-center text-white transition duration-200 group-hover:scale-[1.15]"
                >
                    {title}
                </Title>

                {/* Uncomment when needed */}
                {/* {promotionalTag ? (
                    <div className="absolute bottom-4 left-0 flex w-full w-full items-end justify-between overflow-hidden">
                        <P
                            size="2xs"
                            className=" px-3 text-left font-primary tracking-[-0.3px] text-white"
                        >
                            {parse(promotionalTag)}
                        </P>

                        <ArrowRight
                            className="mx-3 shrink-0 overflow-hidden group-hover:animate-arrow-animation"
                            fill="#ffffff"
                            size={24}
                        />
                    </div>
                ) : null} */}
            </Card>
        </a>
    );
};

export default SubCategoriesCardTemplate;
